import React, { useEffect, useState, Fragment } from 'react';
/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import Container from '../../blocks/Container';
import Panel from '../../../atoms/panels/Panel';
import Button from '../../../atoms/buttons/Button';
import Header from '../../blocks/Header';
import Text from '../../common/Text';
import Checkbox from '../../../atoms/Checkbox';
import TextArea from '../../../atoms/TextArea';
import MasterConfig, { s3BucketContentDir } from '../../../config/Master';
import styled from '@emotion/styled';
import { FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, withStyles } from '@material-ui/core';
import { getBackgroundTheme, getTextColor, getTextType } from '../../../style';
import { fetchSurveyQuestions } from '../../../sdk';
import { sendDeactivationAction } from '../../../actions/authActions';
import { isDarkMode, isEmpty, isQAautomation, isQAautomationEmail } from '../../../utils/helpers';
import Loader from '../../../atoms/LoaderStyled';
import { useNavigate } from 'react-router-dom';
import { mixpanelTrackEvent } from '../../../utils/mixpanel/common';
import { EVENT_NAMES } from '../../../constants/mixpanel';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import HCaptcha from '../../auth/Hcaptcha';
import { CustomStyledProps } from 'style/style';
import breakpoints from 'theme/material/breakpoints';
import useMediaBreakpoints from 'hooks/useMediaBreakpoints';

interface DeactivateProps {
  deactivate: any;
  errors: any;
  fetchQuestions: any;
}

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
const MainPanel = styled(Panel)(
  // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
  () => ({
    position: 'relative',
    marginTop: 50,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: getBackgroundTheme().personalInfo,

    [breakpoints.down(639.05)]: {
      margin: '10px auto',
      width: '100%',
    },
  }),
  ({ darkMode }: CustomStyledProps) => ({
    border: darkMode ? 'none' : '1px solid #DAE1E9',
  })
);

const PanelTitle1 = styled('div')({
  fontSize: 20,
  color: '#F6F4F4',
  display: 'flex',
  alignItems: 'center',
});

const PanelTitle2 = styled('div')({
  fontSize: 20,
  color: '#595959',
  display: 'flex',
  alignItems: 'center',
  lineHeight: '21px',

  [breakpoints.down(479.05)]: {
    fontSize: 18,
  },
});

const FormContainer = styled('div')({
  maxWidth: 600,
  padding: '24px 40px',

  [breakpoints.down(834.05)]: {
    padding: '14px 0',
    margin: '0 100px',
    width: '100%',
  },

  [breakpoints.down(639.05)]: {
    padding: '14px 0 0 0',
    margin: '0',
    width: '100%',
  },
});

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
const FormPanel = styled(Panel)(() => ({
  background: 'linear-gradient(180deg, #E6F6F7 0%, #F6F4F4 100%)',
  padding: 30,
  marginTop: 20,
  border: '2px solid #fff',
  boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.16)',

  [breakpoints.down(479.05)]: {
    padding: 15,
    margin: '15px auto',
    width: 'auto',
    border: '2px solid #fff',
    boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.16)',
    paddingBottom: 30,
  },
}));

const FormTitleBox = styled('div')({
  width: '100%',

  [breakpoints.down(479.05)]: {
    textAlign: 'start',
  },
});

const CustomRadio = withStyles({
  root: {
    padding: 6,
    color: '#2B8FD7',
    '&:checked': {
      color: '#2B8FD7',
    },
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
})(props => <Radio color="default" {...props} />);

const OptionText = styled(Text)(() => ({
  ...getTextColor().DARK_2,
  marginBottom: 0,
  fontSize: 16,
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '21px' /* 131.25% */,
  letterSpacing: '-0.32px',
}));

const LabelText = styled(Text)(() => ({
  ...getTextColor().DARK_2,
  marginBottom: 0,
  fontSize: 14,
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '20px',
  letterSpacing: '-0.28px',
}));

const RedAsterisk = styled.span`
  font-size: 14px;
  color: #e21d4a;
`;

const ActionButtonContainer = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: 30,

  [breakpoints.down('md')]: {
    marginTop: 20,
  },

  [breakpoints.down(479.05)]: {
    flexDirection: 'column-reverse',
    justifyContent: 'space-evenly',
  },
});

const DeactivateTitle = styled('p')(() => ({
  ...getTextType().blockHeader,
  marginTop: 0,
  marginBottom: 0,
}));

const Divider = styled('hr')({
  borderColor: '#8392A7',
  borderStyle: 'dashed',
  marginTop: 30,
  marginBottom: 30,
  borderBottom: 0,
  [breakpoints.down('md')]: {
    marginTop: 20,
    marginBottom: 20,
  },
});

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
const AggrementFormControl = styled(FormControl)({
  display: 'flex !important',
  flexDirection: 'initial !important',
  justifyContent: 'space-between !important',
  [breakpoints.down('md')]: {
    flexDirection: 'column !important',
  },
});

const customButtonStyle = {
  padding: '16px 32px',
  fontSize: 18,
  width: 255,
  height: 48,
  fontWeight: 700,
  textAlign: 'center',
  fontStyle: 'normal',
  lineHeight: '16px',
  borderWidth: 1,
  [breakpoints.down('md')]: {
    fontSize: 14,
    width: 202,
    height: 42,
    fontWeight: 600,
  },
  [breakpoints.down(479.05)]: {
    marginBottom: 15,
  },
};

const CharacterCount = styled('div')({
  fontSize: 12,
  color: '#8f8f8f',
  letterSpacing: '-0.24px',
  display: 'flex',
  justifyContent: 'flex-end',
});

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
const AnimatedDiv = styled('div')(({ toggle }) => ({
  overflow: 'hidden',
  transformOrigin: 'top',
  maxHeight: toggle ? '1000px' : '0',
  opacity: toggle ? 1 : 0,
  transition: 'max-height 0.4s ease-in-out , opacity 0.7s ease-in-out',
}));

const LoaderWrapper = styled('div')({
  position: 'relative',
  height: 110,
});

const HCaptchaContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const MAX_CHARACTER_COUNT = 500;

const Deactivate = ({ deactivate, errors }: DeactivateProps): any => {
  const [question, setQuestion] = useState(null);
  const [options, setOptions] = useState([]);
  const [answer, setAnswer] = useState(0);
  const [improve, setImprove] = useState('');
  const [agreed, setagreed] = useState(false);
  const [surveyAnswer, setSurveyAnswer] = useState('');
  const [captcha, setCaptcha] = useState('');

  const navigate = useNavigate();
  const { is_member_hcaptcha_enabled, cf_country } = useSelector((state: RootState) => state.settings);
  const { email } = useSelector((state: RootState) => state.profile);
  const { isMobile } = useMediaBreakpoints();

  const fetchQuestions = async () => {
    const res = await fetchSurveyQuestions('deactivate_account');

    if (res && res.data) {
      setQuestion(res.data.data);
    }
  };

  useEffect(() => {
    fetchQuestions();
  }, []);

  useEffect(() => {
    mixpanelTrackEvent({ eventName: EVENT_NAMES.DEACTIVATION_PAGE_VIEWED, payload: {} });
  }, []);

  useEffect(() => {
    if (is_member_hcaptcha_enabled && errors?.deactivateAccountFailed) {
      setCaptcha('');
    }
  }, [errors]);

  useEffect(() => {
    if (question) {
      setOptions(question.answers.data);
    }
  }, [question]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const survey_id = question.id;
    const answer_id = answer;
    const raw_answer = options.find(item => item.id === answer_id);

    sendDeactivationAction({
      reason: raw_answer,
      feedback: improve,
    });

    const cameFromDelete = localStorage.getItem('came_from_delete_page') === 'true' ? 1 : 0;

    deactivate({
      survey_id,
      answer_id,
      improve,
      agreed,
      survey_answer: surveyAnswer,
      came_from_delete: cameFromDelete,
      ...(is_member_hcaptcha_enabled && captcha ? { 'h-captcha-response': captcha } : {}),
    });
  };

  const agreeChange = () => {
    setagreed(!agreed);
  };

  const onChangeAnswer = e => {
    setAnswer(parseInt(e.target.value));
    setSurveyAnswer('');
  };

  const onChangeImprove = e => {
    setImprove(e.target.value);
  };

  const handleSurveyAnswerChange = e => {
    const newValue = e.target.value;

    if (newValue.length <= MAX_CHARACTER_COUNT) {
      setSurveyAnswer(newValue);
    }
  };

  const allowSubmit = (): boolean => {
    if (is_member_hcaptcha_enabled && isEmpty(captcha) && !(isQAautomation() || isQAautomationEmail(email ?? ''))) {
      return false;
    }

    return Boolean(answer !== 0) && Boolean(agreed);
  };

  return (
    <Container
      css={{
        [breakpoints.down(639.05)]: {
          padding: 0,
          display: 'flex',
          margin: '10px 10px',
        },
        [breakpoints.down(479.05)]: {
          padding: 0,
          display: 'flex',
          margin: '10px 10px',
        },
      }}
    >
      <MainPanel withHeader darkMode={isDarkMode()}>
        <FormContainer>
          <Header>
            <PanelTitle1 data-test-id="fs-modal-headline">
              <img
                src={`${s3BucketContentDir}cart_abandonment/circle_notifications.svg`}
                height="30"
                width="30"
                alt="Cart Abandonment Icon"
              />
              &ensp;
              <DeactivateTitle>Deactivate Account</DeactivateTitle>
            </PanelTitle1>
          </Header>
          <OptionText>
            Deactivating your account will disable your profile and delete any offers you’ve made or requested. Your
            profile will not show in Search but some information may still be visible to others, such as your username
            and any messages you’ve sent.
            <strong>You can always reactivate your account by logging back in.</strong>
          </OptionText>

          <FormPanel>
            <FormTitleBox>
              <PanelTitle2>
                <strong>We are sorry to see you leave us!</strong>
              </PanelTitle2>
              <br />
              <OptionText>
                If there is anything we can do to help improve what we’re doing at WhatsYourPrice, please let us know by
                filling out the short survey below.
              </OptionText>
            </FormTitleBox>
            <Divider />
            <FormControl style={{ width: '100%' }}>
              {question && options ? (
                <Fragment>
                  <FormLabel>
                    <LabelText>
                      {question && question.content}
                      <RedAsterisk>*</RedAsterisk>
                    </LabelText>
                  </FormLabel>
                  <RadioGroup onChange={onChangeAnswer} value={answer}>
                    {options &&
                      options.map((item, index) => {
                        const content = item.content.replace(':origin', `${MasterConfig.SITE_NAME}`);

                        return (
                          <div key={index}>
                            <FormControlLabel
                              key={item.id}
                              value={item.id}
                              control={<CustomRadio />}
                              label={<LabelText>{content}</LabelText>}
                            />
                            {item.description && (
                              <AnimatedDiv
                                // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
                                toggle={answer === item.id}
                              >
                                <TextArea
                                  placeholder={item.description}
                                  name="survey_answer"
                                  value={surveyAnswer}
                                  id="survey_answer"
                                  onChange={handleSurveyAnswerChange}
                                  resize="vertical"
                                  customInputStyle={{
                                    padding: 10,
                                    overflow: 'unset',
                                    border: 0,
                                    fontSize: 14,
                                    height: 41,
                                  }}
                                  customStyle={{ paddingBottom: 0, marginTop: 5 }}
                                />
                                <CharacterCount>
                                  {surveyAnswer.length}/{MAX_CHARACTER_COUNT}
                                </CharacterCount>
                              </AnimatedDiv>
                            )}
                          </div>
                        );
                      })}
                  </RadioGroup>
                </Fragment>
              ) : (
                <LoaderWrapper>
                  <Loader
                    active={true}
                    background={'#fff'}
                    topPosition={20}
                    style={{ width: '100%', height: 100 }}
                    text=""
                  />
                </LoaderWrapper>
              )}
            </FormControl>
            <Divider />
            <FormControl fullWidth style={{ paddingBottom: 0 }}>
              <FormLabel>
                <LabelText style={{ marginBottom: 4 }}>How can we improve?</LabelText>
              </FormLabel>
              <TextArea
                placeholder="Please provide feedback here..."
                name="improve"
                value={improve}
                id="improve"
                onChange={onChangeImprove}
                resize="vertical"
                customStyle={{ paddingBottom: 0 }}
                customInputStyle={{
                  padding: 10,
                  fontSize: 14,
                  overflow: 'unset',
                  border: 0,
                  height: 80,
                  '::placeholder': {
                    fontSize: 14,
                  },
                }}
              />
            </FormControl>
            <Divider />
            {is_member_hcaptcha_enabled && (
              <HCaptchaContainer style={{ marginTop: 0 }}>
                <HCaptcha
                  cfCountry={cf_country}
                  storeCaptchaResponse={token => setCaptcha(token)}
                  resetCaptcha={errors?.deactivateAccountFailed}
                />
              </HCaptchaContainer>
            )}
            <Divider />
            <AggrementFormControl>
              <Checkbox
                label={<LabelText style={{ display: 'inline-block' }}>I agree with the terms above.</LabelText>}
                type="checkbox"
                onChange={agreeChange}
                value={agreed}
              />
              <FormLabel>
                <LabelText style={{ marginTop: isMobile ? 0 : 10, color: '#E21D4A' }}>*required</LabelText>
              </FormLabel>
            </AggrementFormControl>
            <ActionButtonContainer>
              <Button
                // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
                customStyle={customButtonStyle}
                buttonType="secondaryBlue"
                onClick={() => {
                  navigate('/dashboard');
                }}
              >
                Keep My Account
              </Button>
              <Button
                // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
                customStyle={{ ...customButtonStyle }}
                buttonType="primaryRed"
                onClick={handleSubmit}
                disabled={!allowSubmit()}
              >
                Deactivate My Account
              </Button>
            </ActionButtonContainer>
          </FormPanel>
        </FormContainer>
      </MainPanel>
    </Container>
  );
};

export default Deactivate;
